<template>
  <generic-modal :title="modal_title" ref="generic_modal">
    <!-- Formulario del ticket -->
    <div class="columns">
      <div class="column">
        <div class="field">
          <label class="label">{{ $t("title") }}</label>
          <div class="control">
            <input
              class="input"
              type="text"
              v-model="meta.title"
              :placeholder="$t('title')"
            />
          </div>
        </div>

        <div class="field">
          <label class="label">{{ $t("description") }}</label>
          <div class="control">
            <textarea
              class="textarea"
              v-model="meta.description"
              :placeholder="$t('description')"
              rows="4"
            ></textarea>
          </div>
        </div>

        <div class="field">
          <generic-dropdown
            :title="$t('customer')"
            :placeholder="$t('select_customer')"
            :is_multiple="false"
            url="/customer/catalog"
            name_field="name"
            value_field="customer_id"
            @selected="customer_selected"
            ref="customer_dropdown"
          />
        </div>
      </div>

      <div class="column">
        <div class="field">
          <generic-dropdown
            :title="$t('category')"
            :placeholder="$t('select_category')"
            :is_multiple="false"
            url="/helpdesk/category/catalog"
            name_field="name"
            value_field="category_id"
            @selected="category_selected"
            ref="category_dropdown"
          />
        </div>

        <div class="field">
          <generic-dropdown
            :title="$t('status')"
            :placeholder="$t('select_status')"
            :is_multiple="false"
            url="/helpdesk/status/catalog"
            name_field="name"
            value_field="status_id"
            @selected="status_selected"
            ref="status_dropdown"
          />
        </div>

        <div class="field">
          <generic-dropdown
            :title="$t('priority')"
            :placeholder="$t('select_priority')"
            :is_multiple="false"
            url="/helpdesk/priority/catalog"
            name_field="name"
            value_field="priority_id"
            @selected="priority_selected"
            ref="priority_dropdown"
          />
        </div>

        <div class="field">
          <generic-dropdown
            :title="$t('assigned_agent')"
            :placeholder="$t('select_agent')"
            :is_multiple="false"
            url="/helpdesk/agent/catalog"
            name_field="name"
            value_field="agent_id"
            @selected="agent_selected"
            ref="agent_dropdown"
          />
        </div>

        <div class="field" v-if="meta.uuid">
          <label class="label">{{ $t("resolved_date") }}</label>
          <div class="control">
            <input
              class="input"
              type="datetime-local"
              v-model="meta.resolved_date"
            />
          </div>
        </div>

        <div class="field" v-if="meta.uuid && meta.resolved_date">
          <label class="label">{{ $t("resolution_notes") }}</label>
          <div class="control">
            <textarea
              class="textarea"
              v-model="meta.resolution_notes"
              :placeholder="$t('resolution_notes')"
              rows="3"
            ></textarea>
          </div>
        </div>
      </div>
    </div>

    <!-- Manejo de errores -->
    <div class="notification is-danger mt-5" v-if="error">
      <p v-html="error"></p>
    </div>

    <!-- Footer con botones -->
    <template v-slot:footer>
      <button
        class="button is-primary is-rounded"
        @click="save"
        :disabled="is_form_invalid"
      >
        <span class="icon"><i class="fa fa-save"></i></span>
        <span>{{ $t("save") }}</span>
      </button>
      <button
        class="button is-danger is-rounded"
        @click="remove"
        v-if="meta.uuid"
      >
        <span class="icon"><i class="fa fa-trash"></i></span>
        <span>{{ $t("delete") }}</span>
      </button>
      <button class="button is-rounded" @click="close_modal">
        <span class="icon"><i class="fa fa-close"></i></span>
        <span>{{ $t("cancel") }}</span>
      </button>
    </template>
  </generic-modal>
</template>

<script>
import {
  put,
  post,
  del,
  get,
  generate_error_message_text,
  confirm_deletion_dialog,
} from "@/utils";
import GenericModal from "@/components/generic_modal/Index.vue";
import GenericDropdown from "@/components/generic_dropdown/Index.vue";

export default {
  name: "TicketModal",
  components: { GenericModal, GenericDropdown },
  data() {
    return {
      meta: {},
      error: "",
    };
  },
  computed: {
    is_form_invalid() {
      return !this.meta.title || !this.meta.description;
    },
    modal_title() {
      return this.meta.uuid ? this.$t("edit_ticket") : this.$t("create_ticket");
    },
  },
  methods: {
    close_modal() {
      this.$refs.generic_modal.close_modal();
      this.set_initial_state();
    },

    show_modal(uuid = null) {
      this.set_initial_state();

      if (uuid) {
        get("/helpdesk/ticket", { uuid })
          .then((ticket) => {
            this.meta = ticket;

            // Formatear fecha para input datetime-local
            if (ticket.resolved_date) {
              this.meta.resolved_date = new Date(ticket.resolved_date)
                .toISOString()
                .slice(0, 16);
            }

            this.$refs.generic_modal.show_modal();

            // Establecer valores en dropdowns
            this.$nextTick(() => {
              if (ticket.customer_id && this.$refs.customer_dropdown) {
                this.$refs.customer_dropdown.set_value(ticket.customer_id);
              }
              if (ticket.category_id && this.$refs.category_dropdown) {
                this.$refs.category_dropdown.set_value(ticket.category_id);
              }
              if (ticket.status_id && this.$refs.status_dropdown) {
                this.$refs.status_dropdown.set_value(ticket.status_id);
              }
              if (ticket.priority_id && this.$refs.priority_dropdown) {
                this.$refs.priority_dropdown.set_value(ticket.priority_id);
              }
              if (ticket.assigned_agent_id && this.$refs.agent_dropdown) {
                this.$refs.agent_dropdown.set_value(ticket.assigned_agent_id);
              }
            });
          })
          .catch((error) => {
            this.error = generate_error_message_text(error);
          });
      } else {
        this.$refs.generic_modal.show_modal();
      }
    },

    save() {
      const isEdit = !!this.meta.uuid;
      const method = isEdit ? post : put;
      const endpoint = "/helpdesk/ticket";

      // Convertir fecha de vuelta a formato ISO si existe
      let data = { ...this.meta };
      if (data.resolved_date) {
        data.resolved_date = new Date(data.resolved_date).toISOString();
      }

      method(endpoint, data)
        .then(() => {
          this.$emit("success");
          this.$refs.generic_modal.close_modal();
        })
        .catch((error) => {
          this.error = generate_error_message_text(error);
        });
    },

    remove() {
      confirm_deletion_dialog(this.$t("are_you_sure_delete"), this, () => {
        del("/helpdesk/ticket", { uuid: this.meta.uuid })
          .then(() => {
            this.$emit("success");
            this.$refs.generic_modal.close_modal();
          })
          .catch((error) => {
            this.error = generate_error_message_text(error);
          });
      });
    },

    set_initial_state() {
      this.meta = {
        title: "",
        description: "",
        customer_id: null,
        category_id: null,
        status_id: null,
        priority_id: null,
        assigned_agent_id: null,
        resolved_date: null,
        resolution_notes: "",
      };
      this.error = "";
    },

    customer_selected(customer) {
      this.meta.customer_id = customer ? customer.customer_id : null;
    },

    category_selected(category) {
      this.meta.category_id = category ? category.category_id : null;
    },

    status_selected(status) {
      this.meta.status_id = status ? status.status_id : null;
    },

    priority_selected(priority) {
      this.meta.priority_id = priority ? priority.priority_id : null;
    },

    agent_selected(agent) {
      this.meta.assigned_agent_id = agent ? agent.agent_id : null;
    },
  },
};
</script>
